import {useCallback, useState, useMemo, useEffect, useRef} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatThreadStatus, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {
  ChatDraftStatus,
  clearDraftMessageContent,
  getDraftState,
  seedDraftStateFromMessage,
  useChatDraftState,
} from 'llm/chat-library/ChatDraftState';
import {getCompletedUploads} from 'llm/chat-library/ChatFileHelper';
import {
  getLatestApplicationState,
  getLatestPublicDataEnabled,
  getLatestSystemState,
} from 'llm/chat-library/ChatStateUtil';
import {
  ClientDisplayChatEvent,
  ClientOptimisticUserMessage,
  ClientProgressMessage,
  ClientStreamingAssistantMessage,
  DisplayMessageChatEventNode,
  ProgressStatus,
} from 'llm/chat-library/messages/MessageTypes';
import {
  createPendingUserMessage,
  createProgressMessage,
  createStreamingMessage,
} from 'llm/chat-library/messages/MessageUtils';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {
  ToolCallsByAssistantMessage,
  getProgressToolCalls,
  getCurrentExchangeProgressData,
  getToolCallsByAssistantMessage,
} from 'llm/chat-library/progress/ProgressUtils';
import {getAvailableAgentTypes, getDefaultAgentType} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useChatSessionEvents} from 'llm/chat-library/useChatSessionEvents';
import {useMCPEnabledServersOptional} from 'llm/mcp-client-library/MCPEnabledServersContext';
import * as Perf from 'performance/Perf';
import {RealtimeMessage} from 'realtime/RealtimeChannel';
import {useRealtimeRefetch} from 'realtime/RealtimeUpdateSubscriptionWrapper';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';

const EMPTY_STRING_ARRAY: ReadonlyArray<string> = [];

export interface SendMessageParams {
  /** When true, archives previous failed messages before sending */
  retry?: boolean;
}

export interface UseMessageThreadOptions {
  chatSessionId: string;
  /**
   * When true, fetches tool call data from allThreads and derives progressData.
   * Enables activity/progress tracking UI for agents that use tools.
   */
  includeToolCalls?: boolean;
  /**
   * When true, disables realtime update subscriptions.
   * Used for read-only/locked chats where the user shouldn't receive live updates.
   */
  disableRealtimeUpdates?: boolean;
}

export interface UseMessageThreadResult {
  /** Display events including optimistic user messages, streaming messages, and completed thinking sections */
  events: Array<ClientDisplayChatEvent>;
  /** All progress data from the session (used for citations, activity modal). Null if includeToolCalls is false or no data available. */
  progressData: ProgressData | null;
  /** Latest application state from StateUpdateChatEventNode events, derived from raw server events */
  latestApplicationState: object | null;
  isLoading: boolean;
  /** True when assistant is processing (thread status ACTIVE/QUEUED or pending message exists) */
  isAssistantRunning: boolean;
  /** True when submit failed before the agent began processing and the optimistic message is still shown. */
  isSubmitErrorMessage: boolean;
  /**
   * True when the agent has paused and is waiting for structured user input before it can continue.
   * Thread status is AWAITING_USER_INPUT. The agent is NOT running — the user must act.
   * Consumers should read latestApplicationState to get the structured data (e.g. onboarding questions).
   */
  isAwaitingUserInput: boolean;
  /**
   * True when the thread has finished successfully. Thread status is COMPLETED.
   * No further user input or agent activity is expected.
   */
  isCompleted: boolean;
  errorMessage: string | null;
  /** True when the last display message is from the user (indicates a failed processing state) */
  isLastMessageFromUser: boolean;
  sendMessage: (params?: SendMessageParams) => void;
  refetch: () => void;
}

/**
 * Hook for managing chat message threads with realtime updates and streaming support.
 *
 * This hook provides:
 * - GraphQL polling (30s interval) as a fallback for data fetching
 * - Realtime-triggered refetch when backend publishes `llm_chat_update`
 * - Streaming message support for ASK (`llm_response_chunk`, `llm_response_complete`)
 * - Optimistic rendering of user messages
 * - Progress data derivation when `includeToolCalls` is enabled
 * - Completed thinking sections inserted before assistant messages
 *
 * NOTE: For optimal UX, the backend should call `LLMChatControllerSQLA.commit_and_publish_chat_update`
 * after significant state changes (tool calls, status changes, etc.) to trigger immediate refetches
 * rather than waiting for the 30s polling interval.
 */
export function useMessageThread({
  chatSessionId,
  includeToolCalls = false,
  disableRealtimeUpdates = false,
}: UseMessageThreadOptions): UseMessageThreadResult {
  const askFallbackAgentType = BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT
    ? AgentType.ASSISTANT
    : AgentType.ASK;
  // ============================================
  // 0. MCP Server IDs
  // ============================================
  const mcpEnabledServers = useMCPEnabledServersOptional();
  // Falls back to empty if the enabled-servers query hasn't resolved yet.
  // This is intentional: messages sent before MCP data loads simply won't include MCP sources.
  const mcpServerIds = mcpEnabledServers?.enabledServerIds ?? EMPTY_STRING_ARRAY;

  // ============================================
  // 1. Pending User Message (optimistic rendering)
  // ============================================
  // Derive pending user message from draft state when status is PENDING.
  // This provides optimistic rendering while the message is being sent to the server.
  // The replyToId and retryOfId fields are used to:
  // - Clear the optimistic message when its persisted version appears (detected by comparing replyToId)
  // - Filter out the original message when showing a retry (using retryOfId)
  const [draftState] = useChatDraftState(chatSessionId);

  const pendingUserMessage: ClientOptimisticUserMessage | null = useMemo(() => {
    // When status is PENDING, we're sending a message - show optimistic UI
    if (draftState?.status === ChatDraftStatus.PENDING) {
      return createPendingUserMessage({
        text: draftState.message.text,
        fileUploads: getCompletedUploads(draftState.message.selectedFiles),
        agentType: draftState.settings?.agentType ?? getDefaultAgentType() ?? askFallbackAgentType,
        replyToId: draftState.replyToId ?? null,
        retryOfId: draftState.retryOfId ?? null,
      });
    }
    return null;
  }, [draftState, askFallbackAgentType]);

  const hasAgentTypeError = draftState.agentTypeError != null;
  const failedUserMessage: ClientOptimisticUserMessage | null = useMemo(() => {
    if (draftState.failedUserMessage == null) {
      return null;
    }
    return createPendingUserMessage({
      text: draftState.failedUserMessage.text,
      fileUploads: getCompletedUploads(draftState.failedUserMessage.selectedFiles),
      agentType: draftState.settings?.agentType ?? getDefaultAgentType() ?? askFallbackAgentType,
      replyToId: draftState.replyToId ?? null,
      retryOfId: draftState.retryOfId ?? null,
    });
  }, [draftState, askFallbackAgentType]);

  // ============================================
  // 2. Streaming State
  // ============================================
  const [streamingMessage, setStreamingMessage] = useState<ClientStreamingAssistantMessage | null>(null);
  const receivedChunks = useRef<Map<number, string>>(new Map());
  const timeToFirstTokenPerfId = useRef<Perf.PerfId | null>(null);

  // ============================================
  // 3. Server Events via GraphQL
  // ============================================
  // The replyToId field on the pending message (stored in draft state) tells us the last display
  // message ID (user or assistant) at the time the user sent their message. We use this to detect
  // when the optimistic message has been persisted: when we see a user message that follows the
  // replyToId position, we know the new message has been persisted and can clear the draft.
  //
  // This approach is robust because:
  // - The replyToId is stored in the draft state (redux-like), not a ref, so it has the same
  //   lifecycle as the optimistic message itself
  // - It directly answers: "has a new user message appeared after the last display message?"
  // - Agents that publish realtime updates frequently won't cause premature clearing because the replyToId comparison is stable
  const {
    events: serverEvents,
    isLoading,
    errorMessage,
    threadStatus,
    refetch,
    chatSession,
    chatSessionQueryData,
    chatType,
  } = useChatSessionEvents({
    chatSessionId,
    includeToolCalls,
    onEventsUpdated: (events) => {
      // Get the current draft state to check if we're waiting for an optimistic message to be persisted
      const currentDraft = getDraftState(chatSessionId);
      if (currentDraft?.status === ChatDraftStatus.PENDING) {
        const replyToId = currentDraft.replyToId ?? null;

        // Check if there's a user message that follows the replyToId.
        // replyToId points at the last DisplayMessageChatEventNode (any role) when we sent the message.
        // Using findLastIndex since relevant IDs are near the end of the thread (close to O(1) in common case).
        // Handles both the case for initial message (replyToIdIndex == -1) or follow-up (replyToIdIndex >= 0)
        const replyToIdIndex = events.findLastIndex(
          (event) =>
            replyToId != null && event.__typename === 'DisplayMessageChatEventNode' && replyToId === event.id
        );
        const hasUserMessageAfterReplyTo =
          events.findLastIndex(
            (event, idx) =>
              idx > replyToIdIndex &&
              event.__typename === 'DisplayMessageChatEventNode' &&
              event.role === LLMRole.USER
          ) !== -1;

        if (hasUserMessageAfterReplyTo) {
          clearDraftMessageContent(chatSessionId);
        }
      }

      // Clear streaming message if server has the final assistant message
      if (events.length > 0) {
        const lastDisplayEvent = events.findLast((e) => e.__typename === 'DisplayMessageChatEventNode');
        if (lastDisplayEvent && lastDisplayEvent.role === LLMRole.ASSISTANT) {
          setStreamingMessage(null);
          receivedChunks.current.clear();
        }
      }
    },
  });
  const serverDisplayEvents = useMemo(
    () =>
      serverEvents.filter(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      ),
    [serverEvents]
  );
  const isAgentSubmitErrorMessage = failedUserMessage != null && hasAgentTypeError;

  // ============================================
  // 4. Realtime-Triggered Refetch
  // ============================================
  const isThreadActive = threadStatus === ChatThreadStatus.ACTIVE || threadStatus === ChatThreadStatus.QUEUED;
  // Note: AWAITING_USER_INPUT is intentionally excluded — the agent is paused waiting for the user.
  useRealtimeRefetch({
    apiId: chatSessionId,
    refetch,
    messageFilter: 'no_filter',
    hasIncompleteDataShouldRefetch: isThreadActive,
    disabled: disableRealtimeUpdates,
  });

  // ============================================
  // 5. Streaming Support
  // ============================================
  const handleStreamingChunk = useCallback(
    (data: {chunkNumber: number; content: string; userEventId: string}) => {
      // Report time-to-first-token metric on first chunk
      if (timeToFirstTokenPerfId.current && data.chunkNumber === 0) {
        Perf.markEnd(timeToFirstTokenPerfId.current);
        Perf.stop(timeToFirstTokenPerfId.current);
        timeToFirstTokenPerfId.current = null;
      }

      receivedChunks.current.set(data.chunkNumber, data.content);

      // Check if we have all chunks in sequence
      let completeContent = '';
      let hasAllChunks = true;
      for (let i = 0; i < receivedChunks.current.size; i++) {
        if (receivedChunks.current.has(i)) {
          completeContent += receivedChunks.current.get(i);
        } else {
          hasAllChunks = false;
          break;
        }
      }

      // Only render if we have all chunks in sequence
      if (hasAllChunks) {
        setStreamingMessage(
          createStreamingMessage({
            createdAt: new Date().toISOString(),
            agentType: getDefaultAgentType() ?? askFallbackAgentType,
            responseToEventId: data.userEventId,
            streamingContent: `${completeContent}▊`,
          })
        );
      }
    },
    [askFallbackAgentType]
  );

  const handleStreamingComplete = useCallback(
    (data: {content: string; userEventId: string; chatId: string}) => {
      setStreamingMessage(
        createStreamingMessage({
          createdAt: new Date().toISOString(),
          agentType: getDefaultAgentType() ?? askFallbackAgentType,
          responseToEventId: data.userEventId,
          streamingContent: data.content,
        })
      );
      receivedChunks.current.clear();

      // NB: We do not need to manually refetch messages at this point: the backend will broadcast a realtime
      // message, which will trigger a refetch via useRealtimeRefetch.
    },
    [askFallbackAgentType]
  );

  const onRealtimeMessage = useCallback(
    (message: RealtimeMessage) => {
      if (message.name === 'llm_response_chunk') {
        handleStreamingChunk(message.data);
      } else if (message.name === 'llm_response_complete') {
        handleStreamingComplete(message.data);
      }
    },
    [handleStreamingChunk, handleStreamingComplete]
  );

  useRealtimeChannelSubscription(
    !disableRealtimeUpdates ? getRealtimeChannelNameForApiId(chatSessionId) : '',
    onRealtimeMessage,
    'no_filter'
  );

  // ============================================
  // 6. Assistant Running State
  // ============================================
  const isAwaitingUserInput = threadStatus === ChatThreadStatus.AWAITING_USER_INPUT;
  const isCompleted = threadStatus === ChatThreadStatus.COMPLETED;
  const isAssistantRunning = isThreadActive || (pendingUserMessage != null && !isAgentSubmitErrorMessage);

  // ============================================
  // 6.5. Seed Draft State from Loaded Thread
  // ============================================
  // When the chat is loaded from the server, seed the draft state with the agent mode
  // from the last display message if it hasn't been set yet.
  // This allows Chat.tsx to use the correct agent mode for the loaded chat.
  const hasSeededDraftState = useRef(false);
  useEffect(() => {
    if (hasSeededDraftState.current || isLoading || !chatType) {
      return;
    }

    const lastDisplayEvent = serverEvents.findLast(
      (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
    );

    // Seed the draft state with settings from the last message.
    // If no messages exist, use the first available agent type for this chat type.
    const agentType = lastDisplayEvent?.agentType ?? getAvailableAgentTypes([chatType])[0] ?? null;
    if (agentType) {
      seedDraftStateFromMessage(chatSessionId, {
        agentType,
        publicDataEnabled: getLatestPublicDataEnabled(serverEvents),
      });
    }

    hasSeededDraftState.current = true;
  }, [chatSessionId, serverEvents, isLoading, chatType]);

  // ============================================
  // 7. All Progress Data (for citations, activity modal)
  // ============================================
  const progressData = useMemo((): ProgressData | null => {
    if (!includeToolCalls || !chatSession) {
      return null;
    }

    const toolCalls = getProgressToolCalls(chatSession);
    if (toolCalls.length === 0) {
      return null;
    }

    return {toolCalls};
  }, [includeToolCalls, chatSession]);

  // ============================================
  // 8. Current Exchange Progress Data (for in-progress thinking)
  // ============================================
  const currentProgressData = useMemo((): ProgressData | null => {
    if (!includeToolCalls) {
      return null;
    }

    // When we have a pending message but no server events yet, return empty progress
    if ((pendingUserMessage || failedUserMessage) && !chatSessionQueryData) {
      return {toolCalls: []};
    }

    if (!chatSessionQueryData) {
      return null;
    }

    return getCurrentExchangeProgressData(chatSessionQueryData, serverEvents);
  }, [includeToolCalls, chatSessionQueryData, serverEvents, pendingUserMessage, failedUserMessage]);

  // ============================================
  // 9. Tool Calls By Assistant Message (for completed thinking sections)
  // ============================================
  const toolCallsByAssistantMessage = useMemo((): ToolCallsByAssistantMessage => {
    if (!includeToolCalls || !chatSessionQueryData) {
      return {};
    }
    return getToolCallsByAssistantMessage(chatSessionQueryData, serverEvents);
  }, [includeToolCalls, chatSessionQueryData, serverEvents]);

  // ============================================
  // 10. Latest Application State
  // ============================================
  // Derive application state from raw serverEvents (not filtered events) to include StateUpdateChatEventNode
  const latestApplicationState = useMemo(() => getLatestApplicationState(serverEvents), [serverEvents]);

  // ============================================
  // 11. Progress Message (for in-progress or error states)
  // ============================================
  const progressMessage = useMemo((): ClientProgressMessage | null => {
    if (isAgentSubmitErrorMessage) {
      return null;
    }

    const systemState = getLatestSystemState(serverEvents);
    const systemErrorMessage = systemState?.error_message;

    if (systemErrorMessage) {
      const lastDisplayEvent = serverEvents.findLast(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      );
      return createProgressMessage({
        status: ProgressStatus.FAILED,
        errorMessage: systemErrorMessage,
        agentType: lastDisplayEvent?.agentType ?? null,
        progressData: currentProgressData,
      });
    }

    // Show progress if last message is from user (either pending or persisted)
    const lastDisplayEvent =
      pendingUserMessage ??
      failedUserMessage ??
      serverEvents.findLast(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      );

    if (lastDisplayEvent && lastDisplayEvent.role === LLMRole.USER) {
      // Use IN_PROGRESS when we have tool calls or thread is active, STARTED otherwise
      const hasToolCalls = currentProgressData && currentProgressData.toolCalls.length > 0;
      const status =
        hasToolCalls || threadStatus === 'ACTIVE' ? ProgressStatus.IN_PROGRESS : ProgressStatus.STARTED;

      return createProgressMessage({
        status,
        errorMessage: null,
        agentType: lastDisplayEvent.agentType,
        progressData: currentProgressData,
      });
    }

    return null;
  }, [
    serverEvents,
    pendingUserMessage,
    failedUserMessage,
    threadStatus,
    currentProgressData,
    isAgentSubmitErrorMessage,
  ]);

  // ============================================
  // 12. Compose Final Events (with completed thinking sections)
  // ============================================
  const events = useMemo<Array<ClientDisplayChatEvent>>(() => {
    const result: Array<ClientDisplayChatEvent> = [];

    // Get the retryOfId from the pending message to filter out the original message being retried
    const retryOfId = pendingUserMessage?.retryOfId ?? failedUserMessage?.retryOfId ?? null;

    // Insert completed thinking sections before each assistant message
    for (const event of serverDisplayEvents) {
      // Filter out the original message when showing a retry to avoid duplicate display
      if (retryOfId != null && event.id === retryOfId) {
        continue;
      }

      if (event.role === LLMRole.ASSISTANT) {
        const toolCallsForMessage = toolCallsByAssistantMessage[event.id];
        if (toolCallsForMessage && toolCallsForMessage.toolCalls.length > 0) {
          const thinkingMessage: ClientProgressMessage = createProgressMessage({
            status: ProgressStatus.COMPLETED,
            errorMessage: null,
            agentType: event.agentType,
            progressData: toolCallsForMessage,
            associatedMessageId: event.id,
          });
          result.push(thinkingMessage);
        }
      }
      result.push(event);
    }

    // Append optimistic user message if present
    if (pendingUserMessage) {
      result.push(pendingUserMessage);
    }
    if (failedUserMessage) {
      result.push(failedUserMessage);
    }

    // Insert streaming message if present (and we don't already have the final message)
    if (streamingMessage) {
      result.push(streamingMessage);
    }

    // Append progress message for in-progress or error states
    if (progressMessage) {
      result.push(progressMessage);
    }

    return result;
  }, [
    serverDisplayEvents,
    pendingUserMessage,
    failedUserMessage,
    streamingMessage,
    progressMessage,
    toolCallsByAssistantMessage,
  ]);

  // ============================================
  // 13. Last Message From User Detection
  // ============================================
  // Check if the chat is in an error state where the last message is from the user
  // and there's no assistant response. This indicates a failed processing state
  // where retry functionality should be available.
  const isLastMessageFromUser = useMemo(() => {
    const lastDisplayMessage = serverEvents.findLast(
      (event) => event.__typename === 'DisplayMessageChatEventNode'
    );
    return lastDisplayMessage != null && lastDisplayMessage.role === LLMRole.USER;
  }, [serverEvents]);

  // ============================================
  // 14. Send Message
  // ============================================
  const sendMessage = useCallback(
    (params: SendMessageParams = {}) => {
      const {retry = false} = params;

      assert(pendingUserMessage == null, 'Cannot send message while pending user message is present');

      // Read from draft state - it should already have everything from user input
      const draft = getDraftState(chatSessionId);
      assert(draft != null, 'Cannot send message without draft state');
      assert(draft.settings != null, 'Cannot send message without settings');

      const {agentType} = draft.settings;

      // Capture the most recent DisplayMessageChatEventNode at send time as the replyToId.
      // This is stored in the draft state (not a ref) so it has the same lifecycle as the
      // optimistic message. We use this to detect when the optimistic message has been persisted:
      // once a user message appears after this position, we know the optimistic message was saved.
      const lastDisplayMessage = serverEvents.findLast(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      );
      const replyToId = lastDisplayMessage?.id ?? null;

      // If this is a retry, capture the ID of the user message being retried so we can filter it out
      const lastUserMessage = retry
        ? serverEvents.findLast(
            (e): e is DisplayMessageChatEventNode =>
              e.__typename === 'DisplayMessageChatEventNode' && e.role === LLMRole.USER
          )
        : null;
      const retryOfId = lastUserMessage?.id ?? null;

      // Start time-to-first-token tracking for ASK
      if (agentType === AgentType.ASK) {
        const perfId = Perf.start('chat.ask.ttft_ms', {enabled: false}) ?? '';
        timeToFirstTokenPerfId.current = perfId;
        Perf.markStart(perfId);
      }

      // Submit the draft with replyToId and retryOfId for optimistic message tracking
      const messageCount = serverEvents.filter((e) => e.__typename === 'DisplayMessageChatEventNode').length;
      ChatActions.submitDraftMessage(chatSessionId, {
        createParams: {retry, mcpServerIds},
        analytics: {messageCount},
        replyToId,
        retryOfId,
      });
    },
    [chatSessionId, serverEvents, pendingUserMessage, mcpServerIds]
  );

  return {
    events,
    progressData,
    latestApplicationState,
    isLoading,
    isAssistantRunning,
    isSubmitErrorMessage: isAgentSubmitErrorMessage,
    isAwaitingUserInput,
    isCompleted,
    errorMessage,
    isLastMessageFromUser,
    sendMessage,
    refetch,
  };
}
